import { useState } from "react";
import { Camera, FileUp, UploadCloud, X } from "lucide-react";
import { pick } from "@/lib/scegliFile";

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
};

export function DocumentoUploadField({ files, onChange, disabled }: Props) {
  const [isDragging, setIsDragging] = useState(false);

  function scegli(accept: string, capture?: "environment") {
    pick(accept, capture, (picked) => onChange([...files, ...picked]));
  }

  return (
    <div className="space-y-3">
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f, i) => (
            <li
              key={i}
              className="border-2 border-[#4B9B2B]/40 bg-[#4B9B2B]/5 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="text-sm">
                <div className="font-medium">{f.name}</div>
                <div className="text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB</div>
              </div>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                disabled={disabled}
                className="text-slate-500 hover:text-red-600 disabled:opacity-50"
                aria-label={`Rimuovi ${f.name}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Desktop: dropzone unica */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const picked = Array.from(e.dataTransfer.files ?? []);
          if (picked.length) onChange([...files, ...picked]);
        }}
        onClick={() => scegli("application/pdf,image/*")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") scegli("application/pdf,image/*"); }}
        aria-label="Carica documento identità"
        className={`hidden md:block cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging
            ? "border-[#4B9B2B] bg-[#4B9B2B]/5"
            : "border-slate-300 hover:border-[#4B9B2B]"
        }`}
      >
        <UploadCloud className="w-7 h-7 text-slate-400 mx-auto mb-2" />
        <div className="text-sm font-medium">Trascina PDF o foto del documento d&apos;identità</div>
        <div className="text-xs text-slate-500 mt-1">fronte e retro · max 10 MB</div>
      </div>

      {/* Mobile: due bottoni distinti */}
      <div className="md:hidden grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => scegli("image/*", "environment")}
          disabled={disabled}
          className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center bg-white"
        >
          <Camera className="w-6 h-6 mx-auto mb-1 text-[#4B9B2B]" />
          <div className="text-sm font-semibold">Scatta foto</div>
        </button>
        <button
          type="button"
          onClick={() => scegli("application/pdf,image/*")}
          disabled={disabled}
          className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center bg-white"
        >
          <FileUp className="w-6 h-6 mx-auto mb-1 text-slate-600" />
          <div className="text-sm font-semibold">Scegli PDF</div>
        </button>
      </div>
    </div>
  );
}
